import { useState } from "react";
import { Button } from "@a2z/ui";
import { DrawerSheet } from "@/shared/components/dialogs/DrawerSheet";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderStatus, ShipmentStatus } from "@/lib/api/types";
import type { GovernorateView } from "@/lib/api/types";
import { GovernorateMultiSelect } from "@/modules/settings/components/GovernorateMultiSelect";
import type { OrdersSort, OrdersBucketFilter } from "./OrdersHeroFilters";
import { FilterAccordion } from "./orders-filters/FilterAccordion";
import { CheckboxRow } from "./orders-filters/CheckboxRow";
import { RadioRow } from "./orders-filters/RadioRow";
import { DateField } from "./orders-filters/DateField";

export type DateRange = "all" | "today" | "week" | "month";
export type PaymentFilter = "all" | "cod" | "online";

type Props = {
  open: boolean;
  onClose: () => void;
  dateRange: DateRange;
  /**
   * Combined preset handler: sets the preset AND clears any custom date range
   * in a single atomic setSearchParams call (avoids multi-setter race).
   */
  onDatePresetChange: (preset: DateRange) => void;
  payment: PaymentFilter;
  onPaymentChange: (next: PaymentFilter) => void;
  onReset: () => void;
  /**
   * On mobile, the sort selector is removed from the hero (no room), so the
   * drawer surfaces it as a radio group at the top. Pass these only on
   * mobile to enable that section.
   */
  sort?: OrdersSort;
  onSortChange?: (next: OrdersSort) => void;
  /** Phase 4 — Governorate multi-select */
  allGovernorates?: GovernorateView[];
  selectedGovernorates?: string[];
  onGovernoratesChange?: (ids: string[]) => void;
  /** Phase 4 — Custom date range (overrides preset when set) */
  customDateFrom?: string;
  customDateTo?: string;
  /**
   * Combined custom-date handler: sets both from+to AND clears the preset
   * in a single atomic setSearchParams call (avoids multi-setter race).
   * Pass empty strings to clear the custom range.
   */
  onCustomDateRangeChange?: (from: string, to: string) => void;
  /** Phase 4 — Shipment status filter */
  shipmentStatus?: ShipmentStatus | "all";
  onShipmentStatusChange?: (v: ShipmentStatus | "all") => void;
  /**
   * Phase 4.2 — Status refinement within the active bucket.
   * `activeBucket` drives which member statuses are offered.
   * `bucketStatuses` = the member statuses for `activeBucket` (from status-counts
   * response); undefined when activeBucket is "all" → offer all 12 statuses.
   * `refinedStatuses` = currently selected subset (empty = all members).
   */
  activeBucket?: OrdersBucketFilter;
  bucketStatuses?: OrderStatus[];
  refinedStatuses?: OrderStatus[];
  onRefinedStatusesChange?: (statuses: OrderStatus[]) => void;
};

const DATE_OPTIONS: { value: DateRange; labelKey: string }[] = [
  { value: "all", labelKey: keys.orders.dateAllTime },
  { value: "today", labelKey: keys.orders.dateToday },
  { value: "week", labelKey: keys.orders.dateThisWeek },
  { value: "month", labelKey: keys.orders.dateThisMonth },
];

const PAYMENT_OPTIONS: { value: PaymentFilter; labelKey: string }[] = [
  { value: "all", labelKey: keys.common.all },
  { value: "cod", labelKey: keys.orders.paymentMethodCod },
  { value: "online", labelKey: keys.orders.paymentMethodExternalOnline },
];

const SORT_OPTIONS: { value: OrdersSort; labelKey: string }[] = [
  { value: "newest", labelKey: keys.orders.sortNewest },
  { value: "oldest", labelKey: keys.orders.sortOldest },
];

const SHIPMENT_STATUS_OPTIONS: {
  value: ShipmentStatus | "all";
  labelKey: string;
}[] = [
  { value: "all", labelKey: keys.common.all },
  { value: "pending_pickup", labelKey: keys.orders.shipmentStatusPendingPickup },
  { value: "picked_up", labelKey: keys.orders.shipmentStatusPickedUp },
  { value: "in_transit", labelKey: keys.orders.shipmentStatusInTransit },
  { value: "delivered", labelKey: keys.orders.shipmentStatusDelivered },
  { value: "returned", labelKey: keys.orders.shipmentStatusReturned },
  { value: "exception", labelKey: keys.orders.shipmentStatusException },
  { value: "cancelled", labelKey: keys.orders.shipmentStatusCancelled },
  { value: "lost", labelKey: keys.orders.shipmentStatusLost },
  { value: "on_hold", labelKey: keys.orders.shipmentStatusOnHold },
];

/** Compact day-month label for the custom-range summary chip. */
function formatDateShort(value: string | undefined, locale: string): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-GB", {
    day: "numeric",
    month: "short",
  }).format(parsed);
}

/** Map an OrderStatus value to its i18n label key in `keys.orderStatus`. */
function orderStatusLabelKey(status: OrderStatus): string {
  const map: Record<OrderStatus, string> = {
    draft: keys.orderStatus.draft,
    paid_less_than_required: keys.orderStatus.paidLessThanRequired,
    pending_buyer_verification: keys.orderStatus.pendingBuyerVerification,
    pending_buyer_payment: keys.orderStatus.pendingBuyerPayment,
    pending_payment_verification: keys.orderStatus.pendingPaymentVerification,
    pending_seller_confirmation: keys.orderStatus.pendingSellerConfirmation,
    confirmed: keys.orderStatus.confirmed,
    preparing: keys.orderStatus.preparing,
    shipped: keys.orderStatus.shipped,
    delivered: keys.orderStatus.delivered,
    cancelled: keys.orderStatus.cancelled,
    declined: keys.orderStatus.declined,
  };
  return map[status] ?? status;
}

/** All non-draft statuses, in a sensible display order. Used when bucket is "all". */
const ALL_STATUSES_FOR_REFINEMENT: OrderStatus[] = [
  "pending_buyer_payment",
  "pending_buyer_verification",
  "pending_payment_verification",
  "pending_seller_confirmation",
  "paid_less_than_required",
  "confirmed",
  "preparing",
  "shipped",
  "delivered",
  "cancelled",
  "declined",
];

/**
 * Right-side filter drawer for the orders list. Renders as a `DrawerSheet`
 * (bottom sheet on mobile → inline-end side drawer on desktop, per
 * DESIGN_CONTEXT §12) so the page stays visible while filtering.
 *
 * Filters are grouped into collapsible accordions so the panel stays short:
 * each section collapses to its title, surfacing a summary chip of its active
 * value when collapsed.
 */
export function OrdersFiltersDrawer({
  open,
  onClose,
  dateRange,
  onDatePresetChange,
  payment,
  onPaymentChange,
  onReset,
  sort,
  onSortChange,
  allGovernorates,
  selectedGovernorates,
  onGovernoratesChange,
  customDateFrom,
  customDateTo,
  onCustomDateRangeChange,
  shipmentStatus,
  onShipmentStatusChange,
  activeBucket,
  bucketStatuses,
  refinedStatuses,
  onRefinedStatusesChange,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();

  // Each accordion toggles independently; all start collapsed so the panel
  // opens short. Active sections still advertise their value via a summary chip.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const toggle = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const showSort = sort !== undefined && onSortChange !== undefined;
  const showGovernorates =
    allGovernorates !== undefined &&
    selectedGovernorates !== undefined &&
    onGovernoratesChange !== undefined;
  const showCustomDate = onCustomDateRangeChange !== undefined;
  const showShipmentStatus =
    shipmentStatus !== undefined && onShipmentStatusChange !== undefined;

  // Status refinement — shown when the bucket is not "all" (or even all)
  const showStatusRefinement =
    onRefinedStatusesChange !== undefined && activeBucket !== undefined;
  // The statuses to offer: bucket's member statuses, or all non-draft statuses when "all"
  const statusRefinementOptions: OrderStatus[] =
    activeBucket === "all" || !bucketStatuses
      ? ALL_STATUSES_FOR_REFINEMENT
      : bucketStatuses;
  const currentRefinedStatuses = refinedStatuses ?? [];

  const statusRefinementActive = currentRefinedStatuses.length > 0;
  const statusRefinementSummary = statusRefinementActive
    ? currentRefinedStatuses.length === 1
      ? t(orderStatusLabelKey(currentRefinedStatuses[0]!))
      : `${currentRefinedStatuses.length}`
    : null;

  // Toggle a single status in/out of the refinement selection.
  // When the result is the full set (= all options selected), treat it as
  // clearing the refinement (same as "all") to avoid over-fetching.
  const handleStatusToggle = (status: OrderStatus) => {
    if (!onRefinedStatusesChange) return;
    const next = currentRefinedStatuses.includes(status)
      ? currentRefinedStatuses.filter((s) => s !== status)
      : [...currentRefinedStatuses, status];
    // If the user selected all options, treat it as cleared
    const isAll = next.length === statusRefinementOptions.length &&
      statusRefinementOptions.every((s) => next.includes(s));
    onRefinedStatusesChange(isAll ? [] : next);
  };

  // ─── Per-section active state + collapsed summary ──────────────────────────
  const hasCustomDate = Boolean(customDateFrom || customDateTo);
  const dateActive = hasCustomDate || dateRange !== "all";
  const dateSummary = hasCustomDate
    ? [formatDateShort(customDateFrom, locale), formatDateShort(customDateTo, locale)]
        .filter(Boolean)
        .join(" – ")
    : dateRange !== "all"
      ? t(DATE_OPTIONS.find((o) => o.value === dateRange)?.labelKey ?? "")
      : null;

  const paymentActive = payment !== "all";
  const paymentSummary = paymentActive
    ? t(PAYMENT_OPTIONS.find((o) => o.value === payment)?.labelKey ?? "")
    : null;

  const govCount = selectedGovernorates?.length ?? 0;
  const govActive = govCount > 0;
  const govSummary = govActive
    ? t(keys.orders.governoratesSelectedCount, { count: govCount })
    : null;

  const shipmentValue = shipmentStatus ?? "all";
  const shipmentActive = shipmentValue !== "all";
  const shipmentSummary = shipmentActive
    ? t(SHIPMENT_STATUS_OPTIONS.find((o) => o.value === shipmentValue)?.labelKey ?? "")
    : null;

  const sortSummary = showSort
    ? t(SORT_OPTIONS.find((o) => o.value === sort)?.labelKey ?? "")
    : null;

  return (
    <DrawerSheet
      isOpen={open}
      onClose={onClose}
      size="sm"
      icon={<FilterIcon />}
      title={t(keys.orders.filtersAction)}
      subtitle={t(keys.orders.filtersSubtitle)}
      closeLabel={t(keys.orders.closeFilters)}
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={onReset} className="flex-1">
            {t(keys.common.reset)}
          </Button>
          <Button variant="secondary" size="md" onClick={onClose} className="flex-[2]">
            {t(keys.orders.applyFilters)}
          </Button>
        </>
      }
    >
      {showSort ? (
        <FilterAccordion
          id="sort"
          title={t(keys.orders.sectionSortBy)}
          summary={sortSummary}
          open={openSections.sort ?? false}
          onToggle={toggle}
          toggleAria={t(keys.orders.filterSectionToggleAria, {
            section: t(keys.orders.sectionSortBy),
          })}
          activeLabel={t(keys.orders.filterActiveLabel)}
        >
          <div className="flex flex-col gap-1">
            {SORT_OPTIONS.map((o) => (
              <RadioRow
                key={o.value}
                label={t(o.labelKey)}
                active={sort === o.value}
                onClick={() => onSortChange(o.value)}
              />
            ))}
          </div>
        </FilterAccordion>
      ) : null}

      {/* Status refinement — narrow within active bucket */}
      {showStatusRefinement ? (
        <FilterAccordion
          id="statusRefinement"
          title={t(keys.orders.sectionStatusRefinement)}
          summary={statusRefinementSummary}
          active={statusRefinementActive}
          open={openSections.statusRefinement ?? false}
          onToggle={toggle}
          toggleAria={t(keys.orders.filterSectionToggleAria, {
            section: t(keys.orders.sectionStatusRefinement),
          })}
          activeLabel={t(keys.orders.filterActiveLabel)}
        >
          <div className="flex flex-col gap-1">
            {statusRefinementOptions.map((status) => {
              const isSelected = currentRefinedStatuses.includes(status);
              return (
                <CheckboxRow
                  key={status}
                  label={t(orderStatusLabelKey(status))}
                  checked={isSelected}
                  onClick={() => handleStatusToggle(status)}
                />
              );
            })}
          </div>
          {statusRefinementActive ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => onRefinedStatusesChange?.([])}
              className="mt-2"
            >
              {t(keys.common.reset)}
            </Button>
          ) : null}
        </FilterAccordion>
      ) : null}

      {/* Date — presets + custom range grouped together */}
      <FilterAccordion
        id="date"
        title={t(keys.orders.sectionDate)}
        summary={dateSummary}
        active={dateActive}
        open={openSections.date ?? false}
        onToggle={toggle}
        toggleAria={t(keys.orders.filterSectionToggleAria, {
          section: t(keys.orders.sectionDate),
        })}
        activeLabel={t(keys.orders.filterActiveLabel)}
      >
        <div className="flex flex-col gap-1">
          {DATE_OPTIONS.map((o) => (
            <RadioRow
              key={o.value}
              label={t(o.labelKey)}
              active={dateRange === o.value && !hasCustomDate}
              onClick={() => onDatePresetChange(o.value)}
            />
          ))}
        </div>

        {showCustomDate ? (
          <div className="mt-3 flex flex-col gap-3 border-t border-[color:var(--border-light)] pt-3">
            <p className="text-[12px] font-semibold text-[color:var(--text-muted)]">
              {t(keys.orders.sectionCustomDate)}
            </p>
            <div className="grid grid-cols-1 gap-2.5 min-[380px]:grid-cols-2">
              <DateField
                label={t(keys.orders.customDateFrom)}
                value={customDateFrom ?? ""}
                max={customDateTo || undefined}
                onChange={(v) => onCustomDateRangeChange(v, customDateTo ?? "")}
              />
              <DateField
                label={t(keys.orders.customDateTo)}
                value={customDateTo ?? ""}
                min={customDateFrom || undefined}
                onChange={(v) => onCustomDateRangeChange(customDateFrom ?? "", v)}
              />
            </div>
          </div>
        ) : null}
      </FilterAccordion>

      {/* Payment method */}
      <FilterAccordion
        id="payment"
        title={t(keys.orders.sectionPaymentMethod)}
        summary={paymentSummary}
        active={paymentActive}
        open={openSections.payment ?? false}
        onToggle={toggle}
        toggleAria={t(keys.orders.filterSectionToggleAria, {
          section: t(keys.orders.sectionPaymentMethod),
        })}
        activeLabel={t(keys.orders.filterActiveLabel)}
      >
        <div className="flex flex-col gap-1">
          {PAYMENT_OPTIONS.map((o) => (
            <RadioRow
              key={o.value}
              label={t(o.labelKey)}
              active={payment === o.value}
              onClick={() => onPaymentChange(o.value)}
            />
          ))}
        </div>
      </FilterAccordion>

      {/* Governorate multi-select */}
      {showGovernorates ? (
        <FilterAccordion
          id="governorate"
          title={t(keys.orders.sectionGovernorate)}
          summary={govSummary}
          active={govActive}
          open={openSections.governorate ?? false}
          onToggle={toggle}
          toggleAria={t(keys.orders.filterSectionToggleAria, {
            section: t(keys.orders.sectionGovernorate),
          })}
          activeLabel={t(keys.orders.filterActiveLabel)}
        >
          <GovernorateMultiSelect
            allGovernorates={allGovernorates}
            selectedIds={selectedGovernorates}
            onChange={onGovernoratesChange}
          />
        </FilterAccordion>
      ) : null}

      {/* Shipment status */}
      {showShipmentStatus ? (
        <FilterAccordion
          id="shipment"
          title={t(keys.orders.sectionShipmentStatus)}
          summary={shipmentSummary}
          active={shipmentActive}
          open={openSections.shipment ?? false}
          onToggle={toggle}
          toggleAria={t(keys.orders.filterSectionToggleAria, {
            section: t(keys.orders.sectionShipmentStatus),
          })}
          activeLabel={t(keys.orders.filterActiveLabel)}
        >
          <div className="flex flex-col gap-1">
            {SHIPMENT_STATUS_OPTIONS.map((o) => (
              <RadioRow
                key={o.value}
                label={t(o.labelKey)}
                active={shipmentValue === o.value}
                onClick={() => onShipmentStatusChange(o.value)}
              />
            ))}
          </div>
        </FilterAccordion>
      ) : null}
    </DrawerSheet>
  );
}

function FilterIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
